import _ from 'lodash';
// remove: eslint-disable-next-line eb/import-max-depth
import LazyString from 'js-utils/lazy-gettext/LazyString';
import React, { PureComponent } from 'react';
import classNames from 'classnames';

import Divider from 'eventbrite_design_system/divider/Divider';
import Icon from 'eventbrite_design_system/icon/Icon';

import {
    ANNUAL_FEE_TITLE,
    DEFAULT_PACKAGE_TYPES,
    getCappedFeeTitleOverThreshold,
    getCappedFeeTitleUnderThreshold,
    ITEM_TYPES_SHOWN_IN_PRICING_PAGE,
    ROW_TITLE_PER_ITEM_TYPE,
    LOCALES_WITH_MULTIPLE_ITEM_TYPES_PRICES,
} from '../../constants/main';
import {
    ANNUAL_FEE_CUSTOM,
    ANNUAL_FEE_NONE,
    ANNUAL_PRICE_PER_LOCALE,
} from '../../constants/signupFeatures';

import './featureComparisonTable.scss';

import CheckChunkySvg from 'eventbrite_design_system/iconography/icons/CheckChunky';
import MinusSvg from 'eventbrite_design_system/iconography/icons/Minus';

/*
Icon type constants
*/
const AVAILABLE_ICON_COLOR = 'ui-orange';
const UNAVAILABLE_ICON_COLOR = 'grey-500';
const AVAILABLE_ICON_TYPE = <CheckChunkySvg />;
const UNAVAILABLE_ICON_TYPE = <MinusSvg />;

/*
Classname constants
*/
const PRICE_PER_TICKET_PRICING_CELLS_CLASS_NAME =
    'eds-text--center eds-g-cell eds-g-cell-6-12 eds-g-cell-mn-2-12 eds-g-cell-md-2-12 eds-g-cell-mw-2-12 eds-g-cell-ln-2-12 eds-g-cell-lg-2-12 eds-g-cell-lw-2-12';
const FEATURE_AVAILABILITY_ICON_CLASS_NAME =
    'eds-text--center eds-g-cell eds-g-cell-6-12 eds-g-cell-mn-2-12 eds-g-cell-md-2-12 eds-g-cell-mw-2-12 eds-g-cell-ln-2-12 eds-g-cell-lg-2-12 eds-g-cell-lw-2-12';
const FEATURE_AVAILABILITY_STRING_CLASS_NAME =
    'eds-text--center eds-text-bm eds-g-cell eds-g-cell-6-12 eds-g-cell-mn-2-12 eds-g-cell-md-2-12 eds-g-cell-mw-2-12 eds-g-cell-ln-2-12 eds-g-cell-lg-2-12 eds-g-cell-lw-2-12';
const FEATURE_LINE_CONTAINER_CLASS_NAME =
    'eds-g-group eds-l-pad-vert-3 eds-l-pad-top-3 eds-align--center-vertical';
const FEATURE_LINE_DIVIDER_CLASS_NAME =
    'eds-g-offset-0-12 eds-g-offset-mw-1-12 eds-g-cell-12-12 eds-g-cell-mw-10-12 eds-g-cell-ln-10-12 eds-g-cell-lg-10-12 eds-g-cell-lw-10-12';
const FEATURE_LINE_DIVIDER_LONG_CLASS_NAME =
    'eds-g-offset-0-12 eds-g-offset-mw-1-12 eds-g-cell-12-12 eds-g-cell-mw-11-12 eds-g-cell-ln-11-12 eds-g-cell-lg-11-12 eds-g-cell-lw-11-12';
const FEATURE_BLOCK_ICON_CLASS_NAME =
    'eds-g-offset-0-12 eds-g-offset-mw-1-12 eds-g-cell eds-g-cell-1-10 eds-l-pad-right-2';

/*
Price per paid ticket (e.g. 1% + $0.99 Max $19.95 / ticket) cells
*/
const ItemPriceCell = ({
    formattedFee,
    formattedMax,
    taxInformation: { taxDescription, needTaxDescription },
    isSimplePricing,
    showMaxFee,
    planIndex,
}) => {
    const isTabMode = !_.isNil(planIndex);
    const pricingClassName = classNames({
        'eds-text-bl': !isSimplePricing && !needTaxDescription,
        'eds-text-bm': needTaxDescription && !isTabMode,
        'eds-text-bs': isTabMode,
    });
    let perTicketLabel = null;
    let planFeeInfo = (
        <p className={pricingClassName}>
            {`${formattedFee} ${taxDescription}`}
        </p>
    );

    if (showMaxFee && formattedMax && !isSimplePricing) {
        perTicketLabel = (
            <p className="eds-text-bs eds-text--light">{formattedMax}</p>
        );
    }

    if (needTaxDescription) {
        planFeeInfo = (
            <div className="eds-g-cell eds-g-cell-1-1">
                <p className={pricingClassName}>{formattedFee}</p>
                <p className="tax-description-muted">{taxDescription}</p>
            </div>
        );
    }

    if (isTabMode && needTaxDescription) {
        planFeeInfo = (
            <div className="eds-g-cell eds-g-cell-1-1">
                <p className={pricingClassName}>{formattedFee}</p>
                <p className={pricingClassName}>{taxDescription}</p>
            </div>
        );
    }

    return (
        <div
            className={PRICE_PER_TICKET_PRICING_CELLS_CLASS_NAME}
            role="gridcell"
        >
            {planFeeInfo}
            {perTicketLabel}
        </div>
    );
};

/*
Helper function to extract fee components to build multiple table rows with the data.

This probably doesn't need to be split into a helper function,
but having this separate makes an eventual refactor from the double map
to reduce easier.
*/
const _getPricingLinesData = (
    packageTypes = DEFAULT_PACKAGE_TYPES,
    feeBreakdown,
    assortmentsTax,
    isSimplePricing = false,
    showMaxFee = false,
    planIndex = null,
) => {
    const pricePerPaidTicketPerPlanData = packageTypes.map((packageName) => (
        <ItemPriceCell
            key={packageName}
            isSimplePricing={isSimplePricing}
            showMaxFee={showMaxFee}
            taxInformation={assortmentsTax[packageName]}
            {...feeBreakdown[packageName]}
            planIndex={planIndex}
        />
    ));
    const paymentProcessingFeePerPlanData = packageTypes.map(
        (packageName) => feeBreakdown[packageName].formattedProcessingFee,
    );

    const priceUnderCappedFeeThreshold = packageTypes.map((packageName) => {
        const feeProps = {
            ...feeBreakdown[packageName],
        };

        if (feeBreakdown[packageName].formattedCappedFee) {
            feeProps.formattedFee =
                feeBreakdown[packageName].formattedCappedFee;
        }
        return (
            <ItemPriceCell
                key={`${packageName}-capped`}
                isSimplePricing={isSimplePricing}
                {...feeProps}
                taxInformation={assortmentsTax[packageName]}
                planIndex={planIndex}
            />
        );
    });

    return {
        pricePerPaidTicketPerPlanData,
        paymentProcessingFeePerPlanData,
        priceUnderCappedFeeThreshold,
    };
};

/*
Helper function to get annual fee data depending on locale.

Used for Beyond Free smokescreen experiment. When experiment has been concluded,
get data from API instead of hard-coding fees.
*/
const _getAnnualFeeData = (locale, packagesTotal) => {
    let annualFeeData = [
        ANNUAL_FEE_NONE,
        ANNUAL_FEE_NONE,
        ANNUAL_PRICE_PER_LOCALE[locale],
    ];

    if (packagesTotal >= 4) {
        annualFeeData = [...annualFeeData, ANNUAL_FEE_CUSTOM];
    }

    return annualFeeData;
};

/*
Helper function to get threshold value from feebreakdown.

EXPERIMENT_MARKER=LOW_ATV_CAPPED_FEE
*/
const _getCappedFeeThreshold = (feeBreakdown) =>
    Object.values(feeBreakdown)[0].formattedFeeThreshold;

const AssembleItemPriceCell = ({
    packageObject,
    pricingItem: { isSimplePricing, showMaxFee, assortmentsTax, planIndex },
}) =>
    Object.entries(packageObject).map(([packageName, packageValue]) => (
        <ItemPriceCell
            key={packageName}
            isSimplePricing={isSimplePricing}
            showMaxFee={showMaxFee}
            taxInformation={assortmentsTax[packageName]}
            {...packageValue}
            planIndex={planIndex}
        />
    ));

const PricingPerItemType = ({ feeBreakdown, packagesTotal, ...pricingItem }) =>
    Object.entries(feeBreakdown).reduce(
        (acc, [itemType, packageObject]) =>
            ITEM_TYPES_SHOWN_IN_PRICING_PAGE.includes(itemType)
                ? [
                      ...acc,
                      <FeatureLine
                          key={itemType}
                          title={ROW_TITLE_PER_ITEM_TYPE[itemType]}
                          perPlanData={AssembleItemPriceCell({
                              packageObject,
                              pricingItem,
                          })}
                          packagesTotal={packagesTotal}
                      />,
                  ]
                : acc,
        [],
    );

/*
Pricing rows (e.g "Price per paid ticket", "Payment processing")
*/
const PricingBlock = ({
    locale,
    packageTypes,
    packagesTotal,
    feeBreakdown,
    assortmentsTax,
    paymentProcessingFeature,
    planIndex = null,
    extraMainFeatures = [],
    isSimplePricing,
    showMaxFee,
    pricePerFreeTicket,
    pricePerPaidTicket,
    shouldShowAnnualFee,
    shouldShowCappedFee,
    useRefactoredPricingTableComparison,
}) => {
    const {
        pricePerPaidTicketPerPlanData,
        paymentProcessingFeePerPlanData,
        priceUnderCappedFeeThreshold,
    } = _getPricingLinesData(
        packageTypes,
        feeBreakdown.ticket || feeBreakdown,
        assortmentsTax,
        isSimplePricing,
        showMaxFee,
        planIndex,
        useRefactoredPricingTableComparison,
        packagesTotal,
    );
    let pricingPerItemType = null;

    if (
        useRefactoredPricingTableComparison &&
        LOCALES_WITH_MULTIPLE_ITEM_TYPES_PRICES.includes(locale)
    ) {
        pricingPerItemType = (
            <PricingPerItemType
                feeBreakdown={feeBreakdown}
                isSimplePricing={isSimplePricing}
                showMaxFee={showMaxFee}
                assortmentsTax={assortmentsTax}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
        );
    }

    const paymentProcessingFeeLineData = {
        ...paymentProcessingFeature,
        perPlanData: paymentProcessingFeePerPlanData,
    };

    const mainFeaturesShown = extraMainFeatures.map(
        ({ title, perPlanData }, index) => (
            <FeatureLine
                key={`extraMainFeature-${index}`}
                title={title}
                perPlanData={perPlanData}
                packageTypes={packageTypes}
                packagesTotal={packagesTotal}
                planIndex={planIndex}
            />
        ),
    );

    let paymentProcessingLine = null;
    let annualFeeLine = null;
    let cappedFeeLine = null;

    if (!isSimplePricing) {
        paymentProcessingLine = (
            <FeatureLine
                {...paymentProcessingFeeLineData}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
        );
    }

    if (shouldShowAnnualFee) {
        annualFeeLine = (
            <FeatureLine
                title={ANNUAL_FEE_TITLE}
                perPlanData={_getAnnualFeeData(locale, packagesTotal)}
                packagesTotal={packagesTotal}
                planIndex={planIndex}
            />
        );
    }

    // EXPERIMENT_MARKER=LOW_ATV_CAPPED_FEE
    let paidTicketTitle = pricePerPaidTicket;

    if (shouldShowCappedFee) {
        const threshold = _getCappedFeeThreshold(feeBreakdown);

        paidTicketTitle = getCappedFeeTitleOverThreshold(threshold);
        cappedFeeLine = (
            <FeatureLine
                title={getCappedFeeTitleUnderThreshold(threshold)}
                perPlanData={priceUnderCappedFeeThreshold}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
        );
    }

    return (
        <div className="eds-l-pad-bot-4" role="grid">
            {annualFeeLine}
            {cappedFeeLine}
            {pricingPerItemType || (
                <FeatureLine
                    title={paidTicketTitle}
                    perPlanData={pricePerPaidTicketPerPlanData}
                    planIndex={planIndex}
                    packagesTotal={packagesTotal}
                />
            )}
            <FeatureLine
                {...pricePerFreeTicket}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
            {paymentProcessingLine}
            {mainFeaturesShown}
        </div>
    );
};

/*
Icon to represent feature availability (e.g. a check mark)
*/
const FeatureAvailabilityIcon = ({ isAvailable }) => {
    const iconType = isAvailable ? AVAILABLE_ICON_TYPE : UNAVAILABLE_ICON_TYPE;
    const iconColor = isAvailable
        ? AVAILABLE_ICON_COLOR
        : UNAVAILABLE_ICON_COLOR;

    return (
        <div className={FEATURE_AVAILABILITY_ICON_CLASS_NAME} role="gridcell">
            <Icon type={iconType} size="small" color={iconColor} />
        </div>
    );
};

/*
String to represent feature availability (e.g. "Unlimited")
*/
const FeatureAvailabilityString = ({ availablityString }) => (
    <div className={FEATURE_AVAILABILITY_STRING_CLASS_NAME} role="gridcell">
        <p className="eds-l-pad-hor-2">{availablityString}</p>
    </div>
);

/*
This function enables planData elements to be "any" (HTML/node, string, bool) type.

This isn't strictly necessary, but provides additional functionality if we want to
render other kinds of things for each plan down the road (like a string with custom formatting).
*/
const _getFeatureAvailabilityCell = (isAvailable, index = 0) => {
    let featureAvailabilityCell = null;

    if (React.isValidElement(isAvailable)) {
        featureAvailabilityCell = isAvailable;
    } else if (_.isString(isAvailable) || isAvailable instanceof LazyString) {
        featureAvailabilityCell = (
            <FeatureAvailabilityString
                key={`isAvailable-${index}`}
                availablityString={isAvailable}
            />
        );
    } else {
        featureAvailabilityCell = (
            <FeatureAvailabilityIcon
                key={`isAvailable-${index}`}
                isAvailable={isAvailable}
            />
        );
    }

    return featureAvailabilityCell;
};

/*
A complete feature line, including a label and availability data for each plan
*/
const FeatureLine = ({
    title,
    link,
    perPlanData,
    planIndex = null,
    packagesTotal,
}) => {
    let availabilityInfo = null;
    let featureTitle = title;
    const featureLineTitleClasses = classNames(
        'eds-text--left eds-g-offset-0-12 eds-g-offset-mw-1-12 eds-g-cell eds-g-cell-6-12',
        {
            'eds-g-cell-mw-4-12 eds-g-cell-ln-4-12 eds-g-cell-lg-4-12 eds-g-cell-lw-4-12':
                packagesTotal <= 3,
            'eds-g-cell-mn-4-12 eds-g-cell-mw-3-12 eds-g-cell-ln-3-12 eds-g-cell-lg-3-12 eds-g-cell-lw-3-12':
                packagesTotal > 3,
        },
    );

    if (planIndex !== null) {
        const isAvailable = perPlanData[planIndex];

        availabilityInfo = _getFeatureAvailabilityCell(isAvailable);
    } else {
        availabilityInfo = perPlanData.map((isAvailable, index) =>
            _getFeatureAvailabilityCell(isAvailable, index),
        );
    }

    if (link) {
        featureTitle = (
            <a href={link} rel="noopener noreferrer" target="_blank">
                {title}
            </a>
        );
    }

    return (
        <div className="eds-g-grid" role="row">
            <div className={FEATURE_LINE_CONTAINER_CLASS_NAME}>
                <div className={featureLineTitleClasses} role="gridcell">
                    <p>{featureTitle}</p>
                </div>
                {availabilityInfo}
            </div>
            <div
                className={
                    packagesTotal >= 4
                        ? FEATURE_LINE_DIVIDER_LONG_CLASS_NAME
                        : FEATURE_LINE_DIVIDER_CLASS_NAME
                }
            >
                <Divider />
            </div>
        </div>
    );
};

/*
A collection of feature lines (e.g. "Start Selling")
*/
const FeaturesBlock = ({
    heading,
    iconType,
    features,
    planIndex,
    packagesTotal,
}) => {
    const blockIcon = (
        <Icon type={iconType} size="small" color={AVAILABLE_ICON_COLOR} />
    );
    const featureLines = features.map(({ title, link, perPlanData }) => (
        <FeatureLine
            key={`${title.toString()}`}
            title={title}
            link={link}
            perPlanData={perPlanData}
            planIndex={planIndex}
            packagesTotal={packagesTotal}
        />
    ));

    return (
        <div role="grid">
            <div className="eds-l-pad-bot-6 eds-l-pad-top-10 eds-align--center-vertical">
                <div className={FEATURE_BLOCK_ICON_CLASS_NAME}>{blockIcon}</div>
                <section className="eds-g-cell eds-g-cell-4-12">
                    <h1 className="eds-text-bl eds-text--left eds-text-weight--heavy">
                        {heading}
                    </h1>
                </section>
            </div>
            {featureLines}
        </div>
    );
};

/*
A helper function that iterates over the collections of features defined in
the settings and generates Feature Blocks from the data
*/
const _getFeaturesBlocks = (sections, planIndex, packagesTotal) =>
    _.toArray(sections).map(({ heading, iconType, features }) => (
        <FeaturesBlock
            key={iconType.type.displayName}
            heading={heading}
            iconType={iconType}
            features={features}
            planIndex={planIndex}
            packagesTotal={packagesTotal}
        />
    ));

export default class FeatureComparisonTable extends PureComponent {
    render() {
        const {
            locale,
            packageTypes,
            feeBreakdown,
            assortmentsTax,
            paymentProcessingFeature,
            sections,
            planIndex,
            isSimplePricing,
            showMaxFee,
            extraMainFeatures,
            pricePerPaidTicket,
            pricePerFreeTicket,
            shouldShowAnnualFee,
            shouldShowCappedFee,
            packagesTotal,
            useRefactoredPricingTableComparison,
        } = this.props;
        // locale is being passed for the BF smokescreen.
        // when experiment has concluded, please remove locale and use
        // international settings instead
        const features = _getFeaturesBlocks(sections, planIndex, packagesTotal);

        const featureComparisonTableClassName = classNames(
            'marketing-container__max-width',
            'eds-text-color--grey-600',
            'eds-l-pad-hor-4',
            'eds-l-mn-pad-hor-6',
            'eds-l-md-pad-hor-6',
            {
                'eds-l-pad-bot-20': !isSimplePricing,
            },
        );

        return (
            <div className={featureComparisonTableClassName} role="grid">
                <PricingBlock
                    locale={locale}
                    packageTypes={packageTypes}
                    packagesTotal={packagesTotal}
                    feeBreakdown={feeBreakdown}
                    assortmentsTax={assortmentsTax}
                    paymentProcessingFeature={paymentProcessingFeature}
                    planIndex={planIndex}
                    isSimplePricing={isSimplePricing}
                    showMaxFee={showMaxFee}
                    extraMainFeatures={extraMainFeatures}
                    pricePerPaidTicket={pricePerPaidTicket}
                    pricePerFreeTicket={pricePerFreeTicket}
                    shouldShowAnnualFee={shouldShowAnnualFee}
                    shouldShowCappedFee={shouldShowCappedFee}
                    useRefactoredPricingTableComparison={
                        useRefactoredPricingTableComparison
                    }
                />
                {features}
            </div>
        );
    }
}
